import React, { useCallback, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import styles from "./HorizontalScrollRow.module.scss";

interface HorizontalScrollRowProps {
  children: React.ReactNode;
  className?: string;
}

export default function HorizontalScrollRow({
  children,
  className,
}: HorizontalScrollRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, children]);

  const scrollBy = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <div className={classnames(styles.wrapper, className)}>
      <div ref={scrollRef} className={styles.scrollContainer}>
        {children}
      </div>
      {canScrollLeft && (
        <button
          type="button"
          className={classnames(styles.scrollButton, styles.scrollButtonLeft)}
          onClick={() => scrollBy("left")}
          aria-label="Scroll left"
        >
          <i className="fa-light fa-chevron-left" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          className={classnames(styles.scrollButton, styles.scrollButtonRight)}
          onClick={() => scrollBy("right")}
          aria-label="Scroll right"
        >
          <i className="fa-light fa-chevron-right" />
        </button>
      )}
    </div>
  );
}
